import styled, { css } from 'styled-components';

import type { FontAwesomeIconProps } from '@hivebrite/design-system/FontAwesomeIcon';
import { FontAwesomeIcon } from '@hivebrite/design-system/FontAwesomeIcon';
import { focusOutline } from '@hivebrite/design-system/Theme';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

const ToolbarButton = ({
  iconName,
  IconComponent,
  text,
  disabled,
  className,
  value,
  dataTestId,
  iconProps,
}: {
  iconName?: FontAwesomeIconProps['iconName'];
  IconComponent?: React.ReactNode;
  text: string;
  disabled?: boolean;
  className?: string;
  value?: string;
  dataTestId?: string;
  iconProps?: Partial<FontAwesomeIconProps>;
}) => {
  const isMobileDevice = useIsMobileDevice();

  return (
    <Tooltip childrenElement={text} initialPosition={POSITIONS.TOP}>
      <ButtonStyled
        tabIndex={0}
        aria-disabled={disabled}
        disabled={disabled}
        aria-label={text}
        $isDesktop={!isMobileDevice}
        type="button"
        className={className}
        value={value}
        data-testid={dataTestId}
      >
        {IconComponent}
        {iconName ? (
          <FontAwesomeIcon iconName={iconName} height="16px" {...iconProps} />
        ) : null}
      </ButtonStyled>
    </Tooltip>
  );
};

export default ToolbarButton;

const getDesktopStyle = () =>
  css`
    &:not([disabled]):hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  `;

const ButtonStyled = styled.button<{ $isDesktop: boolean }>`
  ${({ $isDesktop }) => css`
    ${focusOutline}
    display: flex;
    padding: 0;
    margin: 2px;
    background-color: transparent;
    cursor: pointer;
    color: ${({ theme }) => theme.input.color};
    border: 1px solid transparent;
    &:disabled {
      opacity: 0.6;
      cursor: unset;
    }
    &.ql-active {
      color: ${({ theme }) => theme.colors.primary};
      border-radius: 3px;
    }
    ${$isDesktop && getDesktopStyle}
    height: 32px;
    align-items: center;
  `}
`;
